import "./index.css";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Checkbox, Grid, Typography } from "@mui/material";
import PhonePay from "../../assets/phonepe-payment.svg";
import GooglePay from "../../assets/gpay.svg";
import Paytm from "../../assets/paytm-payment.svg";
import { setPaymentMode } from "../../store/order.store";
import { AddAddress, AddressList, AddressSummary } from "./templates";
import {
  useCreateOrderQuery,
  useDeleteAddressMutation,
  useGetAddresses,
  useOrderEventMutation,
  usePlaceOrderMutation
} from "../../hooks";

const shippingAddressStyle = {
  height: "auto",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  boxSizing: "border-box",
  marginTop: "10px",
  padding: "8px 8px 0px 8px",
  borderRadius: "6px"
};

export const AddressLoadingComp = memo(() => (
  <div className="skel-address">
    <div className="skel-address-inside">
      <div className="skel-divide">
        <div className="skel-address-input">
          <span className="span-input skeleton" />
        </div>
        <div className="skel-heading1">
          <h1 className="skeleton"> </h1>
          <h1 className="skeleton"> </h1>
          <h5 className="skeleton"> </h5>
        </div>
      </div>
      <div className="skel-delete">
        <span className="skeleton" />
      </div>
    </div>
  </div>
));

function AddressComponent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const addIds = useMemo(
    () => Array.from({ length: 10 }).map(() => uuidv4()),
    []
  );
  const {
    status: addressStatus,
    data: addresses,
    isError: addressError
  } = useGetAddresses();
  const { status: createStatus } = useCreateOrderQuery(dispatch);
  const { mutate: deleteAddress } = useDeleteAddressMutation();
  const { mutateAsync: orderEventMutationAsync, status: eventStatus } =
    useOrderEventMutation();
  const { mutateAsync: placeOrderMutationAsync, status: placeStatus } =
    usePlaceOrderMutation();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const states = useMemo(() => JSON.parse(process.env.REACT_APP_STATES), []);
  const [isShipSummary, setIsShipSummary] = useState(false);
  const [isBillSummary, setIsBillSummary] = useState(false);

  const { config, cart } = useSelector((state) => ({
    config: state.config,
    cart: state.cart
  }));

  const shippingAmount = useMemo(() => {
    if (
      cart.cartDetails?.totalAmount >
      (config?.shipping_charge_max_threshold ?? 499)
    )
      return 0;
    return config?.shipping_charge_amount ?? 50;
  }, [config, cart]);

  const isCodEnable = useMemo(
    () =>
      config?.cod &&
      cart.cartDetails.finalAmount + shippingAmount <= config.max_cod_amount,
    [config, cart, shippingAmount]
  );

  const [shippingAddressId, setShippingAddressId] = useState(null);
  const [billingAddressId, setBillingAddressId] = useState(shippingAddressId);
  const [isBillingEnabled, setIsBillingEnabled] = useState(
    shippingAddressId === billingAddressId
  );
  const [addAddress, setAddAddress] = useState({ type: undefined });

  const shippingSummary = useMemo(
    () => addresses?.find((each) => each?.id === shippingAddressId),
    [shippingAddressId, addresses]
  );
  const billingSummary = useMemo(
    () => addresses?.find((each) => each?.id === billingAddressId),
    [billingAddressId, addresses]
  );

  const enablePay = useMemo(() => {
    if (!isBillingEnabled) return !!(billingAddressId && shippingAddressId);

    return !!(billingAddressId || shippingAddressId);
  }, [isBillingEnabled, shippingAddressId, billingAddressId]);

  const maxHeight = useMemo(() => {
    let maxH = isMobile ? "82%" : "88%";
    if (isMobile && !(!shippingAddressId || isBillingEnabled)) {
      maxH = "285px";
    } else if (!(!shippingAddressId || isBillingEnabled)) {
      maxH = "300px";
    }

    return maxH;
  }, [isMobile, isBillingEnabled, shippingAddressId]);

  const continueToPaymentDekstop = useCallback(
    async (paymentMode) => {
      if (!["cod", "credit"].includes(paymentMode))
        throw new Error("Invalid input argument!");

      const state = { shippingAddressId, billingAddressId };
      if (isBillingEnabled) delete state.billingAddressId;

      if (paymentMode === "cod") {
        dispatch(setPaymentMode("cod"));
      } else if (paymentMode === "credit") {
        dispatch(setPaymentMode("credit"));
      }
      await orderEventMutationAsync(state);
      if (paymentMode === "credit")
        await placeOrderMutationAsync({ paymentMode });

      return navigate("/payment", { state });
    },
    [isBillingEnabled, shippingAddressId, billingAddressId]
  );

  const continueToPayment = useCallback(async () => {
    const state = { shippingAddressId, billingAddressId };
    if (isBillingEnabled) delete state.billingAddressId;
    await orderEventMutationAsync(state);
    return navigate("/choose-type", { state });
  }, [isBillingEnabled, shippingAddressId, billingAddressId]);

  const handleAddNewAddress = useCallback((type) => {
    setAddAddress({ type });
  }, []);

  useEffect(() => {
    if (addAddress?.type) {
      delete shippingAddressStyle.background;
    } else {
      shippingAddressStyle.background = "#f4f4f4";
    }
  }, [addAddress]);

  useEffect(() => {
    if (addresses?.length === 0) {
      setAddAddress({ type: "shipping" });
    }

    if (addresses?.length === 1 && !isBillingEnabled) {
      setAddAddress({ type: "billing" });
    } else if (addresses?.length !== 1 && !isBillingEnabled) {
      setAddAddress({ type: undefined });
    }
  }, [addresses, isBillingEnabled]);

  if (addressStatus === "pending" || createStatus === "pending") {
    return (
      <div className="address">
        <div className="address-child" />
        <div className="address-heading-container">
          <div>
            <h2 className="shipping-heading">
              {isMobile ? "Addresses" : "Select Shipping Address"}
            </h2>
          </div>
          <div>
            <button
              type="button"
              className="add-new-address-btn"
              onClick={() => handleAddNewAddress("shipping")}
            >
              {isMobile ? "+Add Address" : "Add New Address"}
            </button>
          </div>
        </div>
        <div className="skel-multi-address">
          {addIds.map((id) => (
            <AddressLoadingComp key={id} />
          ))}
        </div>
      </div>
    );
  }

  if (addressError) return "...error";

  const selectShippingAddress = (e) => {
    setShippingAddressId(parseInt(e.target.value, 10));
  };

  const selectBillingAddress = (e) => {
    setBillingAddressId(parseInt(e.target.value, 10));
  };

  const delAddress = (id) => {
    deleteAddress(id);
  };

  const handleBillingEnable = (e) => {
    setIsBillingEnabled(e.target.checked);

    if (e.target.checked) {
      setAddAddress({ type: undefined });
    }
  };

  const handleChangeBilling = () => {
    if (addAddress.type === "billing") {
      setAddAddress({ type: undefined });
    }
    setIsBillSummary(false);
    if (shippingAddressId) setIsShipSummary(true);
  };

  const handleChangeShipping = () => {
    if (addAddress.type === "shipping") {
      setAddAddress({ type: undefined });
    }
    if (!billingAddressId) {
      setIsBillingEnabled(true);
      setShippingAddressId(null);
    }
    if (billingAddressId) setIsBillSummary(true);
    setIsShipSummary(false);
  };

  const handleAddAddressSubmit = () => {
    setAddAddress({ type: undefined });
  };

  return (
    <div className="address">
      <div className="address-height">
        <div className="address-heading-container">
          <div>
            <h2 className="shipping-heading">
              {isMobile ? "Addresses" : "Shipping Address"}
            </h2>
          </div>
          <div>
            {!(isShipSummary || addAddress.type === "shipping") && (
              <div
                className="add-new-address-btn"
                onClick={() => handleAddNewAddress("shipping")}
              >
                {isMobile ? "+Add Address" : "Add New Address"}
              </div>
            )}
            {(isShipSummary || addAddress.type === "shipping") && (
              <button
                type="button"
                className="add-new-address-btn"
                onClick={handleChangeShipping}
              >
                Change Address
              </button>
            )}
          </div>
        </div>

        {isShipSummary ? (
          <AddressSummary address={shippingSummary} states={states} />
        ) : (
          <Grid style={{ ...shippingAddressStyle, maxHeight }}>
            {addAddress.type !== "shipping" && (
              <AddressList
                addresses={[...(addresses || [])]}
                delAddress={delAddress}
                selectAddress={selectShippingAddress}
                onSelect={(id) => setShippingAddressId(id)}
                selectedAddressId={shippingAddressId}
                suffix="shipping_address_list"
                states={states}
              />
            )}
            {addAddress.type === "shipping" && (
              <AddAddress onSubmitExtra={handleAddAddressSubmit} />
            )}
          </Grid>
        )}

        <Grid
          className="address-checkbox"
          style={{
            color: shippingAddressId ? "black" : "lightgray",
            pointerEvents: shippingAddressId ? "all" : "none"
          }}
        >
          <Checkbox
            size="small"
            disabled={!shippingAddressId}
            checked={isBillingEnabled}
            onChange={handleBillingEnable}
          />
          <Typography variant="body2" className="billing-address ">
            Billing address same as Shipping address
          </Typography>
        </Grid>

        {!isBillingEnabled && (
          <>
            <div className="address-heading-container">
              <div>
                <h2 className="shipping-heading">
                  {isMobile ? "Addresses" : "Billing Address"}
                </h2>
              </div>
              <div>
                {!(isBillSummary || addAddress.type === "billing") && (
                  <div
                    className="add-new-address-btn"
                    onClick={() => handleAddNewAddress("billing")}
                  >
                    {isMobile ? "+Add Address" : "Add New Address"}
                  </div>
                )}
                {(isBillSummary || addAddress.type === "billing") && (
                  <button
                    type="button"
                    className="add-new-address-btn"
                    onClick={handleChangeBilling}
                  >
                    Change Address
                  </button>
                )}
              </div>
            </div>

            {isBillSummary ? (
              <AddressSummary address={billingSummary} states={states} />
            ) : (
              <div
                style={{
                  ...shippingAddressStyle,
                  maxHeight: isMobile ? "285px" : "300px"
                }}
              >
                {addAddress.type !== "billing" && (
                  <AddressList
                    addresses={addresses?.filter(
                      (address) => address.id !== shippingAddressId
                    )}
                    delAddress={delAddress}
                    selectAddress={selectBillingAddress}
                    onSelect={(id) => setBillingAddressId(id)}
                    selectedAddressId={billingAddressId}
                    suffix="billing_address_list"
                    states={states}
                  />
                )}
                {addAddress.type === "billing" && (
                  <AddAddress onSubmitExtra={handleAddAddressSubmit} />
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="payment-container">
        {!addAddress?.type && !isMobile && (
          <div className="dekstop-btn fixed_adr_btn">
            <div className="btn-container">
              <button
                type="button"
                className={`pay-online-dekstop ${!enablePay ? "disabled" : ""}`}
                onClick={() => continueToPaymentDekstop("credit")}
                disabled={
                  placeStatus === "pending" || eventStatus === "pending"
                }
              >
                <h3>Pay Online</h3>
                <div className="pay-online">
                  <img src={Paytm} alt="Paytm" className="paytm" />
                  <img src={PhonePay} alt="PhonePay" className="phonePay" />
                  <img src={GooglePay} alt="GooglePay" />
                </div>
              </button>

              {isCodEnable && (
                <button
                  type="button"
                  className={`pay-online ${!enablePay ? "disabled" : ""}`}
                  onClick={() => continueToPaymentDekstop("cod")}
                  disabled={
                    placeStatus === "pending" || eventStatus === "pending"
                  }
                >
                  <h3>Order via COD</h3>
                </button>
              )}
            </div>
          </div>
        )}
        {!!addAddress?.type && (
          <div className="adding-button adding_btn_fixed">
            <button
              id="add-new-address-btn"
              type="submit"
              form="add_address_form"
            >
              Save & Continue
            </button>
            {!!addresses?.length && !isMobile && (
              <button
                id="cancel-add-new-address-btn"
                type="button"
                onClick={() => setAddAddress({ type: undefined })}
              >
                Cancel
              </button>
            )}
          </div>
        )}
        {isMobile && !!shippingAddressId && !addAddress.type && (
          <div className="adding-button">
            <button
              type="button"
              id="add-new-address-btn"
              onClick={continueToPayment}
              disabled={eventStatus === "pending"}
            >
              Save & Continue
            </button>
          </div>
        )}
        {isMobile && !shippingAddressId && !addAddress.type && (
          <div className="adding-button">
            <button type="button" id="add-new-address-btn">
              Select an address
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddressComponent;
